
//#region Util Imports

import * as $ from 'jquery'
import * as _ from 'underscore'

//#endregion

//#region Angular Imports

import { StateService } from "@uirouter/angularjs"

//#endregion

//#region DevExtreme Imports

import 'devextreme/ui/toolbar'
import DevExpress from 'devextreme'

//#endregion

import { ColumnModel } from '../api/api.models'

import { GridTypes } from '../common/grid/grid.controller'
import { IMRSelectPopoverOptions, IIMRSelectPopover } from '../common/select-popover2/select-popover.controller'

import { GridService } from '../../services/grid'

import { IUserSession, ToastHelper, LocalizationHelper, LogHelper, ConvertHelper, TemplateHelper, ConfigHelper, TypeHelper } from '../../helpers'

import { OpLocation } from '../../models/core'
import { Enums } from '../../models/common'
import { ColumnInfo } from '../../models/custom'

import { IMRCashierPanelLocatonDetailsOptions, IIMRCashierPanelLocatonDetails } from './components'
import { CashierPanelService } from './services'
import { IMRBaseControlDirectiveController } from '../common/base'


export interface CashierPanelScope extends ng.IScope {
    isModuleLoading: boolean
    isLocationLoading: boolean

    cashierPanelTitle: string

    titleToolbarOptions: DevExpress.ui.dxToolbarOptions
    locationSelectPopoverOptions: IMRSelectPopoverOptions<OpLocation>
    cashierPanelLocationDetailsOptions: IMRCashierPanelLocatonDetailsOptions
}

export class CashierPanelController extends IMRBaseControlDirectiveController {

    // Consts

    private static get LOG_PREFIX(): string { return '[SIMAX.Module.CashierPanel]' }

    // Consts End


    // Members 

    private consumerLocationId: number

    private selectLocationButton: DevExpress.ui.dxButton
    private refreshLocationButton: DevExpress.ui.dxButton
    private locationSelectPopover: IIMRSelectPopover<OpLocation>
    private locationDetailsComponent: IIMRCashierPanelLocatonDetails

    // Members End


    // Ctor

    constructor(
        private $scope: CashierPanelScope,
        private userSession: IUserSession,
        private $stateParams: StateService,
        private gridService: GridService,
        private cashierPanelService: CashierPanelService
    ) {
        super()

        this.$scope.isModuleLoading = true
        this.setDefaults()

        this.initializeTitleToolbar()
            .then(() => {
                return $.when(this.initializeConsumerLocationDetails(), this.initializeConsumerLocationSelectPopover())
            })
            .then(() => {
                const idLocation: number = _.first(this.$stateParams['ids']) as number

                if (idLocation != null) {
                    this.consumerLocationId = idLocation
                    this.refresh()
                } else {
                    const lastConsumerLocationId: number = this.cashierPanelService.loadLastConsumerLocationIdFromSettings()

                    if (lastConsumerLocationId != null) {
                        this.consumerLocationId = lastConsumerLocationId
                        this.refresh()
                    }
                }
            })
            .fail((error: any) => {
                ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(CashierPanelController.LOG_PREFIX, `Error initializing module.`, error)
            })
            .always(() => {
                this.$scope.isModuleLoading = false
            })
    }

    // Ctor End


    // Component 

    private refresh(): void {
        if (this.consumerLocationId != null) {
            this.getConsumerLocation(this.consumerLocationId)
                .then((location: OpLocation) => {
                    this.setLocation(location)
                })
                .fail(() => {
                    this.reset()
                })
        }
    }

    private setLocation(location: OpLocation): void {
        this.$scope.cashierPanelTitle = location.toString()
        this.cashierPanelService.saveLastConsumerLocationToSettings(location.idLocation)
        this.toggleToolbarDisabled(false)

        if (this.locationDetailsComponent != null) {
            this.locationDetailsComponent.setLocation(location)
        }
    }

    private reset(): void {
        this.$scope.cashierPanelTitle = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SelectConsumer)
        this.cashierPanelService.saveLastConsumerLocationToSettings(null)
        this.toggleToolbarDisabled(true)

        if (this.locationDetailsComponent != null) {
            this.locationDetailsComponent.setLocation(null)
        }
    }

    // Component End


    // Consumer Location

    private getConsumerLocation(idLocation: number): JQueryPromise<OpLocation> {
        this.$scope.isLocationLoading = true

        return this.userSession
            .getDataProvider().Consumer
            .getConsumerLocation(idLocation)
            .then((location: OpLocation) => {
                return location == null ? $.Deferred().reject(null) : location
            })
            .fail((error: any) => {
                const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(CashierPanelController.LOG_PREFIX, `Error downloading consumer location for id: ${idLocation}.`, error)
            })
            .always(() => {
                this.$scope.isLocationLoading = false
            })
    }

    // Consumer Location End


    // Title Toolbar

    private initializeTitleToolbar(): JQueryPromise<void> {
        const selectButtonOptions: DevExpress.ui.dxButtonOptions = {
            template: TemplateHelper.getIconWithText(
                LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),
                ConfigHelper.Icon.USER,
                this.userSession
            ),

            onInitialized: (e: any) => this.selectLocationButton = e.component,
            onClick: () => this.toggleLocationPopover()
        }

        const refreshButtonOptions: DevExpress.ui.dxButtonOptions = {
            disabled: true,
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RefreshLocation),
            template: TemplateHelper.getIcon(ConfigHelper.Icon.REFRESH, this.userSession),

            onInitialized: (e: any) => this.refreshLocationButton = e.component,
            onClick: () => this.refresh()
        }

        this.$scope.titleToolbarOptions = {
            items: [
                {
                    location: 'before',
                    widget: 'dxButton',
                    options: selectButtonOptions
                },
                {
                    location: 'before',
                    widget: 'dxButton',
                    options: refreshButtonOptions
                },
                {
                    location: 'center',
                    template: 'cashierPanelTitle'
                }
            ]
        }

        return TypeHelper.onInitializedAll(this.$scope.titleToolbarOptions, selectButtonOptions)
    }

    private toggleToolbarDisabled(disable: boolean): void {
        if (this.refreshLocationButton != null) {
            this.refreshLocationButton.option('disabled', disable)
        }
    }

    // Title Toolbar End


    // Consumer Location Details

    private initializeConsumerLocationDetails() {
        this.$scope.cashierPanelLocationDetailsOptions = {
            onInitialized: (locationDetails: IIMRCashierPanelLocatonDetails) => this.locationDetailsComponent = locationDetails,
            onRefreshRequest: () => this.refresh()
        }

        return TypeHelper.onInitializedAll(this.$scope.cashierPanelLocationDetailsOptions)
    }

    // Consumer Location Details End


    // Location Select Popover

    private initializeConsumerLocationSelectPopover(): JQueryPromise<any> {
        return this.gridService
            .getColumnModelByCategory(
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__POPUP_GRID_PARAMS,
                Enums.SimaxModuleName.Location
            )
            .then((columns: ColumnModel[]) => {
                this.$scope.locationSelectPopoverOptions = {
                    gridOptions: {
                        idReferenceType: Enums.ReferenceType.IdLocation,
                        columnsDictionary: columns,
                        type: GridTypes.PAGINATED_SMALL,
                        name: null,
                        buttonsOptions: { enabled: true },

                        getDataSourceMethod: (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) =>
                            this.userSession
                                .getDataProvider().Consumer
                                .getCashierPanelLocationFilter(columns, pageNbr, pageSize, customWhereClause),
                    },

                    target: this.selectLocationButton.element(),
                    width: 1200,

                    onInitialized: (imrSelectPopover: IIMRSelectPopover<OpLocation>) => this.locationSelectPopover = imrSelectPopover,
                    onObjectSelected: (object: OpLocation) => {
                        if (object != null) {
                            this.consumerLocationId = object.idLocation
                            this.refresh()
                        }
                    }
                }

                return TypeHelper.onInitializedAll(this.$scope.locationSelectPopoverOptions)
            })
    }

    private toggleLocationPopover(): void {
        if (this.locationSelectPopover != null) {
            this.locationSelectPopover.togglePopover()
        }
    }

    // Location Select Popover End


    // Options

    private setDefaults(): void {
        this.$scope.cashierPanelTitle = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SelectConsumer)
    }

    // Options End
}
